import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons'
import { REGISTRATION_SOURCE } from '@community_dev/types/lib/api/RegistrationSource'
import { useQuery } from '@tanstack/react-query'
import { Alert, Spin, Timeline } from 'antd'
import React, { type JSX } from 'react'

import { SettingsCard, SettingsCardContent, SettingsPage, SettingsRows } from '../Settings'
import { StyledSpinContainer } from '../styled'

import { DailySegmentLimitRow, MessageLimitRow, Subscription } from './components'
import { Account } from './components/Account'
import { PassThroughFees } from './components/PassThroughFees'
import { Twilio } from './components/Twilio'
import { useSubscription } from './queries'
import { TwilioTrustHubTCRData } from './TwilioTrustHubTCRData'

import { getClient } from 'api/clients'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES } from 'constants/queries'
import { useClientHasCapability } from 'hooks/useClientHasCapability'

export const ONBOARDING_TASKS = Object.freeze({
  WELCOME_MESSAGE: 'Welcome Message',
  CONTACT_PHOTO: 'Contact Photo',
  CONFIRMATION_MESSAGE: 'Confirmation Message',
  VOICEMAIL: 'Voicemail',
})

export function Billing({ client }: any): JSX.Element {
  const { isInitialLoading, isError, error, data: subscription } = useSubscription(client.id)
  const individualClient = useQuery([QUERIES.CLIENT, { id: client.id }], () => getClient(client.id))
  const hasEstimateCampaignCost = useClientHasCapability(client.id, CAPABILITIES.FEATURE.CAMPAIGN_COST_ESTIMATION.ALL)

  const isCommunityCSPClient = individualClient.data?.registrationSource === REGISTRATION_SOURCE.COMMUNITY

  if (isInitialLoading || individualClient.isLoading) {
    return (
      <StyledSpinContainer>
        <Spin size="large" />
      </StyledSpinContainer>
    )
  }

  return (
    <SettingsPage description="Plan, subscription, sending limits and carrier fees for this client." title="Billing">
      {isError && error?.status !== 404 && (
        <Alert message="Sorry could not load billing subscription. Please try again." showIcon type="error" />
      )}
      <Account client={client} />
      <Subscription client={client} subscription={subscription} />
      <SettingsCard title="Sending Limits">
        <SettingsRows>
          <MessageLimitRow client={client} />
          <DailySegmentLimitRow client={client} />
        </SettingsRows>
      </SettingsCard>
      <SettingsCard title="Carrier & Provider">
        <SettingsRows>
          <Twilio clientId={client.id} />
        </SettingsRows>
      </SettingsCard>
      {!isCommunityCSPClient && <TwilioTrustHubTCRData client={client} individualClient={individualClient} />}
      {hasEstimateCampaignCost && <PassThroughFees clientId={client.id} />}
      <SettingsCard title="Onboarding Steps Completed">
        <SettingsCardContent>
          <Timeline>
            {Object.values(ONBOARDING_TASKS)
              .map((id) => {
                const task = client.onboardingTasks.tasks[id]
                if (!task) return null
                return task.status === 'complete' ? (
                  <Timeline.Item color="green" dot={<CheckCircleFilled style={{ fontSize: '16px' }} />} key={id}>
                    {task.title}
                  </Timeline.Item>
                ) : (
                  <Timeline.Item color="red" dot={<ClockCircleOutlined style={{ fontSize: '16px' }} />} key={id}>
                    {task.title}
                  </Timeline.Item>
                )
              })
              .filter(Boolean)}
          </Timeline>
        </SettingsCardContent>
      </SettingsCard>
    </SettingsPage>
  )
}
